import React, {useState} from "react";
import {Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
        Typography} from "@mui/material";

import {makeCresAxiosFoundationAPI} from "../../util/AxiosFoundationAPI";
import {useSessionContext} from "../../util/SessionContext";
import {approvalMessage} from "../../util/approvalMessages";
import {formatMoment} from "../../util/formatMoment";
import {Grey5, Grey6} from "../../theme/styles";

// The matrix's history: what changed about the requirements, and who changed it.
//
// KEYED TO THE MATRIX, NOT A REVISION, so it reads continuously across them. Somebody asking "when
// did terms start needing two signatures" does not know which revision to look in -- that is the
// question this answers.
//
// Design: cres-t5/docs/approvals/design.md

type Row = Record<string, any>;

/** One entry as a sentence, because a raw JSON diff is not history anybody reads. */
export function describe(entry: Row) {
    const changes = entry.Changes ?? {};
    const parts: string[] = [];

    (changes.Cells ?? []).forEach((cell: Row) => {
        if (cell.Change === "added") {
            parts.push(`${cell.Cell} now needs ${(cell.Approvers ?? []).join(", ") || "nobody"}`);
        } else if (cell.Change === "removed") {
            // WORTH SPELLING OUT. A cell removed means that type releases unwitnessed again, which
            // is the change somebody reviewing this history is most likely looking for.
            parts.push(`${cell.Cell} no longer needs approval `
                + `(was ${(cell.WasApprovers ?? []).join(", ")})`);
        } else {
            parts.push(`${cell.Cell}: ${(cell.WasApprovers ?? []).join(", ")} → `
                + `${(cell.Approvers ?? []).join(", ")}`);
        }
    });

    if (changes.AuthorMaySign) {
        parts.push(changes.AuthorMaySign.To
            ? "authors may now sign their own records"
            : "authors may no longer sign their own records");
    }
    if (changes.RequireDistinctSigners) {
        parts.push(changes.RequireDistinctSigners.To
            ? "signatures must now come from different people"
            : "one person may now fill more than one slot");
    }
    if (changes.IsActive) {
        parts.push(changes.IsActive.To ? "approvals switched on" : "approvals switched off");
    }
    if (changes.ApprovalsActive === false) parts.push("approvals are off in this revision");
    if (changes.From) parts.push(`revised from revision ${changes.From}`);

    return parts;
}

export default function ApprovalMatrixHistory({open, onClose}: any) {
    const [entries, setEntries] = useState<Row[]>([]);
    // BUILT FROM THE SESSION, as every t1 screen does: the token lives on the
    // session context and there is no module-level instance to import here.
    const sessionContext = useSessionContext();
    const api = React.useMemo(
        () => makeCresAxiosFoundationAPI(sessionContext), [sessionContext]);
    const [message, setMessage] = useState<string | null>(null);

    React.useEffect(() => {
        if (!open) return;
        api({method: "GET", url: "t1/v1/object_approval_matrix", params: {History: true}})
            .then(response => setEntries(response.data?.History ?? []))
            .catch(error => setMessage(approvalMessage(error, "Could not load the history.")));
    }, [api, open]);

    return (
        <Dialog open={!!open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{fontSize: 16}}>Approval matrix history</DialogTitle>
            <DialogContent>
                <Typography style={{fontSize: 13, color: Grey5, marginBottom: 14}}>
                    Every change to what this realm requires, across all revisions of the matrix.
                </Typography>

                {message ? (
                    <Typography style={{fontSize: 13, color: "#B42318"}}>{message}</Typography>
                ) : null}

                {entries.length === 0 && !message ? (
                    <Box sx={{border: "1px dashed #D8DDE4", borderRadius: "8px", padding: "24px",
                              textAlign: "center"}}>
                        <Typography style={{fontSize: 13, color: Grey6}}>Nothing recorded yet.</Typography>
                    </Box>
                ) : null}

                {entries.map(entry => (
                    <div key={entry.USID}
                         style={{padding: "10px 0", borderTop: "1px solid #F2F4F7"}}>
                        <div style={{display: "flex", alignItems: "center", gap: 8}}>
                            <Chip size="small" label={entry.Action} sx={{fontSize: 11, height: 20}}/>
                            {entry.Revision ? (
                                <Typography style={{fontSize: 12, color: Grey6}}>
                                    revision {entry.Revision}
                                </Typography>
                            ) : null}
                            <div style={{flex: 1}}/>
                            <Typography style={{fontSize: 12, color: Grey6}}>
                                {entry.By ?? "—"} · {formatMoment(entry.Datetime)}
                            </Typography>
                        </div>
                        {describe(entry).map(line => (
                            <Typography key={line} style={{fontSize: 13, marginTop: 4}}>{line}</Typography>
                        ))}
                    </div>
                ))}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} sx={{textTransform: "none"}}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
